import React from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { getUserInfo, getLoadingInfo } from '../../redux/selectors'
import {
  saveExclusions,
  fetchEmailPreferences,
  updateEmailPreferences,
  saveEmailPreferences,
  fetchDatabasePreferences,
  saveDatabasePreferences,
  fetchFilterResults,
  dismissFirstTimeHelp
} from '../../redux/actions'
import { requesterEmailAddress, isCurrentDateWithinSuspendDates, formatDateForInput } from '../../libs/utils'
import ManageEmailsComponent from './ManageEmailsComponent'
import { isEmpty, cloneDeep } from 'lodash'

export class ManageEmailsContainer extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      exclusions: {
        filter_groups: [],
        suspend_publications: [],
        filter_preferred: false
      },
      isAllWatchlistsSuspended: false,
      isAllGeneralAlertsSuspended: false,
      isAllPublicationsSuspended: false,
      databasePreferences: {}
    }

    // todo: lr:  may need to copy this for the new ones
    this.toggleExclusion = filterGroupName => event => {
      const emailAddress = requesterEmailAddress(this.props)
      const exclusions = Object.assign({}, this.state.exclusions)
      const filterGroups = exclusions.filter_groups.slice()

      /*
        2026-05-02: lr
        this logic disabled per new requirements on ticket: 26427: [Email Preferences] Bug - Email Preference Toggle Incorrectly Disables All Alerts
        https://ipdanalytics.visualstudio.com/Ipd/_boards/board/t/Cross%20Product%20Services/Stories?System.AssignedTo=lrebolloso%40ipdanalytics.onmicrosoft.com&workitem=26427
      */
      // if (event && event.target && event.target.checked === false) {
      //   let othersOff = true
      //   for (const group of filterGroups) {
      //     if (group.name === filterGroupName) continue
      //     if (group.filtered === false) othersOff = false // if filtered is false (meaning the switch is on) then the "others" aren't off
      //   }
      //   if (othersOff === true) {
      //     return this.enableSuspendAll(
      //       formatDateForInput(new Date().toISOString()),
      //       '',
      //       'SYSTEM: User switched off all Weekly Publication email setting toggles',
      //       'Weekly Publication'
      //     )()
      //   }
      // }

      const filterIndex = filterGroups.findIndex(x => x.name === filterGroupName)
      const filterGroup = Object.assign({}, filterGroups[filterIndex])
      filterGroup.filtered = !filterGroup.filtered
      filterGroups[filterIndex] = filterGroup
      exclusions.filter_groups = filterGroups
      this.props.saveExclusions(emailAddress, exclusions)
    }

    // General Alerts: only consider admin groups and suspend type 'General Alerts'
    this.toggleGeneralExclusion = filterGroupName => event => {
      const emailAddress = requesterEmailAddress(this.props)
      const exclusions = { ...this.state.exclusions }
      const filterGroups = exclusions.filter_groups.slice()

      const idx = filterGroups.findIndex(x => x.name === filterGroupName)
      if (idx < 0) return

      // Use the new switch state; ON => filtered=false, OFF => filtered=true
      const checked = !!(event && event.target && event.target.checked)
      filterGroups[idx] = { ...filterGroups[idx], filtered: !checked }

      exclusions.filter_groups = filterGroups
      this.props.saveExclusions(emailAddress, exclusions)
    }

    this.toggleFilterPreferred = event => {
      if (event && event.target && event.target.checked === false) {
        return this.enableSuspendAll(
          formatDateForInput(new Date().toISOString()),
          '',
          'SYSTEM: User switched off both publication email setting toggles',
          'other'
        )()
      }
      const emailAddress = requesterEmailAddress(this.props)
      const exclusions = Object.assign({}, this.state.exclusions)
      exclusions.filter_preferred = !exclusions.filter_preferred
      return this.props.saveExclusions(emailAddress, exclusions)
    }

    this.enableSuspendAll = (startDate, endDate, reason, type) => () => {
      const emailAddress = requesterEmailAddress(this.props)
      const exclusions = Object.assign({}, this.state.exclusions)
      const suspends = exclusions.suspend_publications.slice()
      let suspendIndex = suspends.findIndex(x => x.type === type)
      if (suspendIndex < 0) suspendIndex = suspends.length > 0 ? suspends.length : 0 // if cant find suspension of this type, put it at the end of the array
      const suspend = Object.assign({}, suspends[suspendIndex] || {})
      suspend.begin_date = startDate
      suspend.end_date = endDate
      suspend.reason = reason
      suspends[suspendIndex] = suspend
      suspend.type = type
      exclusions.suspend_publications = suspends
      this.props.saveExclusions(emailAddress, exclusions)
    }

    this.disableSuspendAll = type => () => {
      const emailAddress = requesterEmailAddress(this.props)
      const exclusions = Object.assign({}, this.state.exclusions)
      const suspends = exclusions.suspend_publications.slice()
      let suspendIndex = suspends.findIndex(x => x.type === type)
      if (suspendIndex < 0) return // can't remove it if it's not there
      suspends.splice(suspendIndex, 1)
      exclusions.suspend_publications = suspends
      this.props.saveExclusions(emailAddress, exclusions)
    }

    this.updateDatabasePreferences = (type, url) => event => {
      if (!type) throw new Error('no type found to handle the database preference update request')
      if (!url) throw new Error('no url found to handle the database preference update request')

      const value = (event.target || {}).value
      if (!value || isEmpty(value)) throw new Error('no value found to handle the database preference update request')

      const preferenceMap = cloneDeep(this.state.databasePreferences)
      const preferences = cloneDeep(preferenceMap[type])
      const preferenceValues = (preferences.values || []).slice()

      const currentSelectedIndex = preferenceValues.findIndex(x => x.selected === true)
      const currentSelectedPreference = Object.assign({}, currentSelectedIndex >= 0 && currentSelectedIndex < preferenceValues.length ? preferenceValues[currentSelectedIndex] : {})

      const newSelectedIndex = preferenceValues.findIndex(x => x.id === value)
      const newSelectedPreference = Object.assign({}, newSelectedIndex >= 0 && newSelectedIndex < preferenceValues.length ? preferenceValues[newSelectedIndex] : {})

      if (isEmpty(newSelectedPreference)) return
      if (!isEmpty(currentSelectedPreference)) {
        if (currentSelectedPreference.id === newSelectedPreference.id) return
        currentSelectedPreference.selected = false
        preferenceValues[currentSelectedIndex] = currentSelectedPreference
      }

      newSelectedPreference.selected = true
      preferenceValues[newSelectedIndex] = newSelectedPreference

      preferences.values = preferenceValues
      preferenceMap[type] = preferences

      this.props.saveDatabasePreferences(url, value, preferenceMap)
    }

    this.updateEmailPreferences = (emailPreferences, categories, originalPreferences, filterResults, preferenceWithEmptyResults = null) => {
      this.props.updateEmailPreferences({categories, preferences: emailPreferences, originalPreferences, filterResults, preferenceWithEmptyResults})
    }

    this.saveEmailPreferences = (emailPreferences, categories, savedPreferenceName) => {
      const emailAddress = requesterEmailAddress(this.props)
      this.props.saveEmailPreferences(emailAddress, {categories, preferences: emailPreferences, savedPreferenceName})
    }

    this.fetchFilterResults = (filterIndex) => {
      const emailAddress = requesterEmailAddress(this.props)
      this.props.fetchFilterResults(emailAddress, filterIndex)
    }
  }

  componentDidMount () {
    document.title = 'User Profile - Customize Emails'
    const emailAddress = requesterEmailAddress(this.props)
    // exclusions are populated as a byproduct of fetchEmailPreferences (same schema/filter endpoint) to avoid a duplicate request
    this.props.fetchDatabasePreferences(emailAddress)
    this.props.fetchEmailPreferences(emailAddress)
  }

  static getDerivedStateFromProps (nextProps, prevState) {
     const hasSuspensions = !isEmpty(nextProps.exclusions) && !isEmpty(nextProps.exclusions.suspend_publications)
     const suspendWatchlists = hasSuspensions && nextProps.exclusions.suspend_publications.find(x => x.type === 'Weekly Publication')
     const suspendGeneralAlerts = hasSuspensions && nextProps.exclusions.suspend_publications.find(x => x.type === 'General Alerts')
     const suspendPublications = hasSuspensions && nextProps.exclusions.suspend_publications.find(x => x.type === 'other')
     const isAllWatchlistsSuspended = !isEmpty(suspendWatchlists) && isCurrentDateWithinSuspendDates(suspendWatchlists.begin_date, suspendWatchlists.end_date)
     const isAllGeneralAlertsSuspended = !isEmpty(suspendGeneralAlerts) && isCurrentDateWithinSuspendDates(suspendGeneralAlerts.begin_date, suspendGeneralAlerts.end_date)
     const isAllPublicationsSuspended = !isEmpty(suspendPublications) && isCurrentDateWithinSuspendDates(suspendPublications.begin_date, suspendPublications.end_date)

    return {
      exclusions: nextProps.exclusions,
      isAllWatchlistsSuspended: isAllWatchlistsSuspended,
      isAllGeneralAlertsSuspended: isAllGeneralAlertsSuspended,
      isAllPublicationsSuspended: isAllPublicationsSuspended,
      databasePreferences: nextProps.databasePreferences
    }
  }

  render () {
    const { history, contact, emailPreferences, loadingFilterResults, dismissFirstTimeHelp } = this.props
    const { exclusions, isAllWatchlistsSuspended, isAllGeneralAlertsSuspended, isAllPublicationsSuspended, databasePreferences } = this.state
    const hasFavorites = contact && contact.subscription.productGroups
      .flatMap(x => x.products).filter(x => x.name === 'Pharma' || x.name === 'Biologics')
      .flatMap(x => x.regions).some(x => x.purchased)
    const managingOther = contact && this.props.emailAddress !== contact.emailAddress

    return (
      <ManageEmailsComponent
        history={history}
        contact={contact}
        exclusions={exclusions}
        emailPreferences={emailPreferences}
        updateEmailPreferences={this.updateEmailPreferences}
        saveEmailPreferences={this.saveEmailPreferences}
        toggleExclusion={this.toggleExclusion}
        toggleGeneralExclusion={this.toggleGeneralExclusion}
        toggleFilterPreferred={this.toggleFilterPreferred}
        enableSuspendAll={this.enableSuspendAll}
        disableSuspendAll={this.disableSuspendAll}
        isAllWatchlistsSuspended={isAllWatchlistsSuspended}
        isAllGeneralAlertsSuspended={isAllGeneralAlertsSuspended}
        isAllPublicationsSuspended={isAllPublicationsSuspended}
        databasePreferences={databasePreferences}
        updateDatabasePreferences={this.updateDatabasePreferences}
        hasFavorites={!!hasFavorites}
        managingOther={!!managingOther}
        loadingFilterResults={loadingFilterResults}
        fetchFilterResults={this.fetchFilterResults}
        dismissFirstTimeHelp={dismissFirstTimeHelp}
      />
    )
  }
}

ManageEmailsContainer.propTypes = {
  emailAddress: PropTypes.string,
  exclusions: PropTypes.object,
  emailPreferences: PropTypes.object,
  databasePreferences: PropTypes.object,
  history: PropTypes.object,
  saveExclusions: PropTypes.func,
  fetchEmailPreferences: PropTypes.func,
  updateEmailPreferences: PropTypes.func,
  saveEmailPreferences: PropTypes.func,
  fetchDatabasePreferences: PropTypes.func,
  saveDatabasePreferences: PropTypes.func,
  loadingFilterResults: PropTypes.arrayOf(PropTypes.number).isRequired,
  fetchFilterResults: PropTypes.func.isRequired,
  dismissFirstTimeHelp: PropTypes.func.isRequired
}

const mapStateToProps = state => ({
  emailAddress: getUserInfo(state).emailAddress,
  exclusions: getUserInfo(state).exclusions,
  emailPreferences: getUserInfo(state).emailPreferences,
  databasePreferences: getUserInfo(state).databasePreferences,
  contact: getUserInfo(state).contact,
  loadingFilterResults: getLoadingInfo(state).loadingFilterResults
})

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    saveExclusions,
    fetchEmailPreferences,
    updateEmailPreferences,
    saveEmailPreferences,
    fetchDatabasePreferences,
    saveDatabasePreferences,
    fetchFilterResults,
    dismissFirstTimeHelp
  }, dispatch)
)

const ManageEmailsConnect = connect(mapStateToProps, mapDispatchToProps)(ManageEmailsContainer)

export default ManageEmailsConnect
